/**
 * SINGLE SOURCE OF TRUTH for every business fact on the site.
 *
 * Components must import from here — never hardcode a phone number,
 * email, address, license number, or service name in JSX.
 *
 * Fields set to `null` or marked TODO_CONFIRM are NOT yet verified by the
 * business owner. Components must render them conditionally (`{x && ...}`)
 * so the site never displays an unverified or invented claim.
 */

export const business = {
  legalName: "Mega Electric Services LLC",
  shortName: "Mega Electric",
  tagline: "Licensed electricians for homes and businesses across DFW.",

  // --- Verified contact facts ---
  phone: {
    // Raw digits for tel: links
    raw: "4692678327",
    // Human-readable display form
    display: "(469) 267-8327",
    href: "tel:+14692678327",
  },
  email: {
    address: "info@yourmegaservices.com",
    href: "mailto:info@yourmegaservices.com",
  },
  social: {
    facebook:
      "https://www.facebook.com/profile.php?id=61579556228877",
  },

  // --- Deliberate omissions (decided by the owner 2026-09-22) ---

  // License number: intentionally NOT published. Leave null.
  // The rendering code stays conditional, so setting this to a real
  // "TECL #####" string is all it takes to switch it on everywhere
  // (trust bar, footer, JSON-LD) if that decision ever changes.
  licenseNumber: null,

  // Hours: intentionally NOT published. Availability genuinely shifts with
  // current job load, so a posted schedule would be a promise we cannot keep.
  // Both reference contractors do the same thing, likely for the same reason.
  // We say what IS true instead — see availabilityNote below.
  hours: null,
  emergencyAvailable: null,

  // What we can honestly say about availability in place of posted hours.
  availabilityNote:
    "Availability changes day to day with current job load. Call and we will give you a real arrival window, not a guess.",

  // No physical or walk-in address. This is a mobile service business that
  // works at the customer's property, so there is no storefront to list.
  // Google treats this as a "service area business": it is correct to omit
  // the street address and declare the service area instead, which the
  // LocalBusiness JSON-LD in public/index.html already does via areaServed.
  address: null,

  // Year established — not supplied yet. Safe to fill in whenever known.
  foundedYear: null,

  // Aggregate review data — only render if the owner confirms real numbers.
  reviews: null, // { rating: 5.0, count: 27, platform: "Google", url: "..." }
};

/**
 * Service areas. Confirmed by the owner 2026-09-22.
 *
 * The real footprint is the I-30 corridor: Fort Worth on the west end,
 * east through Dallas, out to Greenville. Work is concentrated in three
 * hot spots rather than spread evenly, so the UI leads with those and
 * presents the wider corridor as coverage rather than as equal focus.
 */
export const serviceAreas = {
  verified: true,
  primaryMetro: "Dallas-Fort Worth",

  // The honest one-line description of the footprint.
  corridor: "Fort Worth east through Dallas to Greenville",

  // Compact form for tight spaces such as the trust bar.
  shortLabel: "Fort Worth to Greenville",

  // Where the work is concentrated. These three were named directly.
  focus: ["Greenville", "Rockwall", "Dallas"],

  // Representative cities along the corridor, grouped west to east so the
  // list reads as a real geography instead of an SEO keyword dump.
  // NOTE: the owner confirmed the CORRIDOR, not each individual city.
  // Worth a 30-second eyeball to drop any you would rather not drive to.
  corridorCities: [
    { group: "West (Fort Worth side)", cities: ["Fort Worth", "Arlington", "Grand Prairie", "Irving"] },
    { group: "Central (Dallas)", cities: ["Dallas", "Garland", "Richardson", "Plano", "Mesquite"] },
    { group: "East (Rockwall and Greenville)", cities: ["Rowlett", "Rockwall", "Heath", "Fate", "Royse City", "Caddo Mills", "Greenville"] },
    { group: "Southeast", cities: ["Sunnyvale", "Forney", "Terrell", "Wylie", "Sachse"] },
  ],
};

/**
 * Canonical service catalog. One entry per service.
 * `slug` drives anchors/routes. `icon` is a lucide icon name (no emoji, ever).
 */
export const services = [
  {
    slug: "electrical-repair",
    title: "Electrical Repair & Troubleshooting",
    short: "Fast diagnosis and repair of faults, dead circuits, and flickering power.",
    icon: "wrench",
    featured: true,
    signs: [
      "Breakers that trip repeatedly",
      "Lights that flicker or dim",
      "Outlets that are warm, scorched, or dead",
      "A burning smell near a panel or fixture",
    ],
  },
  {
    slug: "panel-upgrades",
    title: "Panel Repairs & Upgrades",
    short:
      "Replace outdated or overloaded panels so your home can carry modern demand safely.",
    icon: "square-activity",
    featured: true,
    signs: [
      "A panel older than 25 years",
      "Fuses instead of breakers",
      "No room left for new circuits",
      "Planning an addition, EV charger, or hot tub",
    ],
  },
  {
    slug: "home-rewiring",
    title: "Home Rewiring",
    short:
      "Replace aging or unsafe wiring with code-compliant circuits throughout the home.",
    icon: "cable",
    featured: true,
    signs: [
      "Cloth, aluminum, or knob-and-tube wiring",
      "Two-prong ungrounded outlets",
      "Frequent shocks from switches or plugs",
    ],
  },
  {
    slug: "switches-outlets",
    title: "Switches & Outlets",
    short:
      "Install, replace, and relocate outlets, dimmers, USB receptacles, and GFCI protection.",
    icon: "plug",
    featured: true,
  },
  {
    slug: "home-generators",
    title: "Home Generators",
    short:
      "Standby and portable generator hookups that keep essentials running through an outage.",
    icon: "battery-charging",
    featured: true,
  },
  {
    slug: "surge-protection",
    title: "Whole-House Surge Protection",
    short:
      "Panel-mounted surge protection for appliances, HVAC, and electronics.",
    icon: "shield",
    featured: true,
  },
  {
    slug: "safety-inspections",
    title: "Smoke Detectors & Safety Inspections",
    short:
      "Interconnected smoke and CO detectors, plus full home electrical safety checks.",
    icon: "alarm-smoke",
    featured: false,
  },
  {
    slug: "outdoor-lighting",
    title: "Lighting Installation",
    short:
      "Interior, exterior, landscape, and security lighting designed and installed.",
    icon: "lightbulb",
    featured: false,
  },
  {
    slug: "ceiling-fans",
    title: "Ceiling Fans",
    short:
      "Fan installation and replacement, including new boxes and brace support.",
    icon: "fan",
    featured: false,
  },
];

/**
 * Differentiators. These are claims about how the business operates.
 * Anything requiring owner verification is flagged; unverified entries are
 * filtered out at render time.
 */
export const differentiators = [
  {
    title: "Licensed and insured",
    body: "Every job is performed by a licensed electrician and backed by liability insurance.",
    requiresVerification: true,
  },
  {
    title: "Upfront pricing",
    body: "You approve the price before any work starts. No surprises on the invoice.",
    requiresVerification: true,
  },
  {
    title: "Clean, code-compliant work",
    body: "Work is done to current NEC and local code, and we leave the space the way we found it.",
    requiresVerification: false,
  },
  {
    title: "Straight answers",
    body: "We explain what failed, what it takes to fix it, and what can safely wait.",
    requiresVerification: false,
  },
];

/**
 * How it works. Reduces friction for first-time callers.
 */
export const process = [
  {
    step: 1,
    title: "Call or request service",
    body: "Tell us what is happening. We ask enough questions to bring the right parts.",
  },
  {
    step: 2,
    title: "On-site diagnosis",
    body: "We find the actual cause instead of guessing, and walk you through what we found.",
  },
  {
    step: 3,
    title: "Approve the price",
    body: "You get the cost in writing before any work begins.",
  },
  {
    step: 4,
    title: "Repair and verify",
    body: "We complete the work, test it under load, and clean up before we leave.",
  },
];

/**
 * FAQs. Written to answer real pre-call questions and support SEO.
 * No invented guarantees, warranties, or pricing.
 */
export const faqs = [
  {
    q: "How soon can you get here?",
    a: "Call us and we will give you a real window based on the day's schedule rather than a vague promise. Tell us if you have no power or smell burning so we can prioritize it.",
  },
  {
    q: "Do I need a permit for my job?",
    a: "Panel upgrades, rewiring, and most new circuits require a permit and inspection in DFW cities. Small repairs and fixture swaps usually do not. We will tell you which category your job falls into before we start.",
  },
  {
    q: "Why does my breaker keep tripping?",
    a: "A breaker that trips is doing its job. The usual causes are an overloaded circuit, a short, or a ground fault. Repeated tripping means something needs to be diagnosed, not just reset.",
  },
  {
    q: "Is my panel too old?",
    a: "Age alone is not the deciding factor, but panels over 25 years old, fuse boxes, and certain recalled brands are worth an inspection. We will tell you honestly if yours is still fine.",
  },
  {
    q: "Can you install an EV charger or hot tub circuit?",
    a: "Yes. Both need a dedicated circuit and enough panel capacity. We check your panel's available load first so you are not paying for a circuit it cannot support.",
  },
  {
    q: "What areas do you serve?",
    a: "We cover the I-30 corridor from Fort Worth east through Dallas out to Greenville, with most of our work in Greenville, Rockwall, and Dallas. If you are near that line, call with your address and we will confirm it.",
  },
  {
    q: "What are your hours?",
    a: "Our availability moves with the current job load, so we would rather give you a real arrival window on the phone than post a schedule we cannot keep. Call and we will tell you honestly when we can be there.",
  },
];

const site = {
  business,
  serviceAreas,
  services,
  differentiators,
  process,
  faqs,
};

export default site;
